import Box from "@mui/material/Box";

import MarketingFooter from "@/components/marketing/MarketingFooter";
import PpcFooter from "@/components/marketing/footer/PpcFooter";
import SmallFooter from "@/components/marketing/footer/SmallFooter";
import WordmarkBar from "@/components/marketing/WordmarkBar";
import MarketingHeader from "@/components/marketing/MarketingHeader";
import MarketingShell from "@/components/marketing/MarketingShell";
import MarketingHead from "@/components/marketing/article/MarketingHead";
import { BAR_ROW, BAR_RULE } from "@/components/marketing/header/tokens";
import { WIDE } from "@/components/marketing/tokens";
import type { LandingBlock, LandingPage } from "@/content/landing/types";
import StickyFreeBar, {
  STICKY_TRIGGER_ID,
} from "../../routes/home/-components/StickyFreeBar";
import { heroLayout } from "@/content/landing/heroLayout";
import { layOut } from "@/content/landing/sectionLayout";
import { siteAssetUrl } from "@/content/types";
import LandingBlocks from "./LandingBlocks";
import { LandingPageProvider } from "./LandingPageContext";

/**
 * The landing pages the published site reveals the conversion bar on. The four
 * competitor pages ship its markup too, but no ScrollTrigger is created for it
 * there: measured across their whole scroll, it never leaves the 80px it is
 * parked below the fold at, so those pages show no bar to match.
 */
const STICKY_BAR_PATHS = new Set(["/proxy-server"]);

/**
 * The pages the published site frames with something other than its own bar
 * and footer: one campaign page closes on the small footer, the paid-search
 * pages open on the wordmark alone, or on nothing where their hero carries
 * it, and close on a footer of their own or on nothing, and the enterprise
 * trial form keeps an older bar with the menus alone and closes on nothing.
 */
type Bar = "wordmark" | "menus" | "none";

const CHROME: Record<string, { bar?: Bar; footer?: "small" | "ppc" | "none" }> =
  {
    "/l/static-residential-proxies": { footer: "small" },
    "/l/best-proxies": { bar: "none", footer: "ppc" },
    "/l/best-proxy": { bar: "none", footer: "ppc" },
    "/l/premium-proxy": { bar: "wordmark", footer: "none" },
    "/l/proxy-start": { bar: "wordmark", footer: "none" },
    "/enterprise-form": { bar: "menus", footer: "none" },
  };

function PageBar({ bar }: { bar?: Bar }) {
  if (bar === "none") return null;
  if (bar === "menus") return <MarketingHeader actions={false} />;
  return bar === "wordmark" ? <WordmarkBar /> : <MarketingHeader />;
}

function PageFooter({ footer }: { footer?: "small" | "ppc" | "none" }) {
  if (footer === "none") return null;
  if (footer === "small") return <SmallFooter />;
  return footer === "ppc" ? <PpcFooter /> : <MarketingFooter />;
}

/**
 * The conversion bar and the sentinel it watches. The published page hangs its
 * 560px trigger off the BOTTOM of the hero, not a distance down from the top of
 * it, so the reveal lands wherever the hero happens to end: measured on live,
 * the flip sits on the hero's bottom edge to the pixel at 1440, 991, 767 and
 * 375 alike, which is why it moves by hundreds of pixels between them.
 *
 * That is why this is drawn after the opening block: the anchor's own top edge
 * is the hero's bottom, and the sentinel hangs back up over it.
 */
function StickyBar() {
  return (
    <Box sx={{ position: "relative", height: 0 }}>
      <Box
        id={STICKY_TRIGGER_ID}
        aria-hidden
        sx={{
          position: "absolute",
          inset: "-560px auto auto 0",
          // A column, not a band: the observer needs a box, and nothing but
          // this one's vertical extent is ever read.
          width: "1px",
          height: "560px",
          pointerEvents: "none",
        }}
      />
      <StickyFreeBar />
    </Box>
  );
}

/**
 * Whether the opening block still needs the fixed bar cleared in flow. The bar
 * is drawn over the page, so an opener carrying the full section break already
 * holds it, and clearing it again drops the whole page one bar-height.
 */
function clearsBar(first: LandingBlock | undefined, page: LandingPage) {
  if (first === undefined || first.kind === "tabs") {
    return true;
  }
  // The centred hero is the one that opens on the full break instead.
  return first.kind === "hero" && heroLayout(page).align !== "center";
}

/** Every landing page's chrome: a `LandingPage` record, and blocks under it. */
export default function LandingPageView({ page }: { page: LandingPage }) {
  const blocks = layOut(page);
  const chrome = CHROME[page.path] ?? {};
  // Only the site's fixed bar needs clearing in flow.
  const clear = chrome.bar === undefined && clearsBar(page.blocks[0], page);
  return (
    <MarketingShell>
      <MarketingHead
        title={page.head.title}
        description={page.head.description}
        canonical={siteAssetUrl(page.path)}
        canonicalOverride={page.head.canonical}
        socialTitle={page.head.socialTitle}
      />
      {page.noindex ? <meta name="robots" content="noindex" /> : null}
      <PageBar bar={chrome.bar} />
      <Box
        component="main"
        sx={
          clear
            ? {
                pt: `${BAR_ROW.mobile + BAR_RULE}px`,
                [WIDE]: { pt: `${BAR_ROW.desktop + BAR_RULE}px` },
              }
            : undefined
        }
      >
        {/*
          The opening block draws in a run of its own so the bar's anchor can
          follow it: the bar reveals off that block's bottom edge. The page's
          whole list is still offered to every block, since the published site
          sets some neighbours as one section and a block draws to match.
        */}
        <LandingPageProvider page={page} blocks={blocks}>
          <LandingBlocks blocks={blocks.slice(0, 1)} />
          {STICKY_BAR_PATHS.has(page.path) ? <StickyBar /> : null}
          <LandingBlocks blocks={blocks.slice(1)} />
        </LandingPageProvider>
      </Box>
      <PageFooter footer={chrome.footer} />
    </MarketingShell>
  );
}
